import { SlideshowSelectEvent } from '@theme/events';

/**
 * Wraps the paired media + content slideshows for Media with content slideshow sections.
 * Uses `display: contents` in CSS so children participate in the section grid.
 */
export class MediaWithContentSlideshowSync extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const media = this.querySelector('[data-mwcs-media-slideshow]');
    const content = this.querySelector('[data-mwcs-content-slideshow]');

    if (!(media instanceof HTMLElement) || !(content instanceof HTMLElement)) return;

    /** @type {boolean} */
    let syncing = false;

    /**
     * @param {any} peer
     * @param {number} index
     */
    const mirrorSelection = (peer, index) => {
      if (syncing) return;
      if (typeof peer.select !== 'function') return;
      if (peer.current === index) return;

      syncing = true;

      try {
        peer.select(index, undefined, { animate: false });
      } finally {
        queueMicrotask(() => {
          syncing = false;
        });
      }
    };

    /** @param {Event} event */
    const onSelect = (event) => {
      if (!(event instanceof SlideshowSelectEvent)) return;

      const slideshow = event.currentTarget;
      if (!(slideshow instanceof HTMLElement)) return;

      const index = /** @type {{ detail?: { index?: number } }} */ (event).detail?.index;
      if (typeof index !== 'number') return;

      if (slideshow === media) {
        mirrorSelection(content, index);
      } else if (slideshow === content) {
        mirrorSelection(media, index);
      }
    };

    media.addEventListener(SlideshowSelectEvent.eventName, onSelect, { signal });
    content.addEventListener(SlideshowSelectEvent.eventName, onSelect, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = undefined;
  }
}

if (!customElements.get('media-with-content-slideshow-sync')) {
  customElements.define('media-with-content-slideshow-sync', MediaWithContentSlideshowSync);
}
